import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import EntityPanelSelector, { LAYOUT_MODES } from '@common/components/EntityPanelSelector';
import {
  loadHosts,
  loadMetricsForHosts,
  loadMetricCategories,
  loadMetricsForCategory,
  loadMetrics,
} from '@api/filters';
import * as styles from './MetricFilter.module.css';

/**
 * MetricFilter Component
 * Uses EntityPanelSelector in THREE_COLUMNS_WITH_FILTER mode
 * 
 * Panel 1 (Available Entities): Hosts or Metric Categories list
 * Panel 2 (Select): Metrics for the selected host/category with checkboxes
 * Panel 3 (Selected Entities): Selected metrics grouped by parent host/category
 * 
 * @param {Array} selectedMetricIds - Array of selected metric IDs
 * @param {function} onSelectionChange - Callback when selection changes
 */
function MetricFilter({
  selectedMetricIds,
  onSelectionChange,
}) {
  const { t } = useTranslation();
  const [selectionBy, setSelectionBy] = useState('hosts'); // 'hosts' or 'metricCategories'
  const [availableCategories, setAvailableCategories] = useState([]); // hosts or metric categories
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [metricsForCategory, setMetricsForCategory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  
  // Cache for metrics selected via category browsing (middle panel)
  const metricsWithCategoryRef = useRef({});

  // Load all metrics upfront for displaying selected items
  const { data: allMetrics = [] } = useQuery({
    queryKey: ['allMetrics'],
    queryFn: () => loadMetrics(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Build lookup map from all metrics (pure computation, no side effects)
  const allMetricsMap = useMemo(() => {
    const map = {};
    allMetrics.forEach((metric) => {
      if (metric && metric.id) {
        map[metric.id] = {
          item: metric,
          categoryId: metric.hostId,
          categoryName: metric.hostName || 'Unknown Host',
        };
      }
    });
    return map;
  }, [allMetrics]);

  // Populate ref separately to avoid side effects in useMemo
  useEffect(() => {
    Object.entries(allMetricsMap).forEach(([id, data]) => {
      metricsWithCategoryRef.current[id] = data;
    });
  }, [allMetricsMap]);

  // Load hosts or metric categories based on selection mode
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (selectionBy === 'hosts') {
          const hosts = await loadHosts();
          setAvailableCategories(hosts);
        } else {
          const categories = await loadMetricCategories();
          setAvailableCategories(categories);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        setAvailableCategories([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [selectionBy]);

  // Load metrics for the selected host/category (middle panel)
  useEffect(() => {
    if (!selectedCategoryId) {
      setMetricsForCategory([]);
      return;
    }

    const loadMetricsForSelectedCategory = async () => {
      setLoadingMetrics(true);
      try {
        let metrics = [];
        const selectedCategory = availableCategories.find(
          (c) => c.id === selectedCategoryId
        );
        
        if (selectedCategory) {
          if (selectionBy === 'hosts') {
            metrics = await loadMetricsForHosts([selectedCategory]);
          } else {
            metrics = await loadMetricsForCategory(selectedCategoryId);
          }
          
          setMetricsForCategory(metrics);
          
          // Cache for grouped display in right panel
          metrics.forEach((metric) => {
            metricsWithCategoryRef.current[metric.id] = {
              item: metric,
              categoryId: metric.hostId,
              categoryName: metric.hostName || 'Unknown Host',
            };
          });
        }
      } catch (error) {
        console.error('Error loading metrics:', error);
        setMetricsForCategory([]);
      } finally {
        setLoadingMetrics(false);
      }
    };
    loadMetricsForSelectedCategory();
  }, [selectionBy, selectedCategoryId, availableCategories]);

  // Handle selection mode change
  const handleSelectionByChange = useCallback((value) => {
    setSelectionBy(value);
    setSelectedCategoryId(null);
    setMetricsForCategory([]);
    setAvailableCategories([]);
  }, []);

  const handleCategorySelect = useCallback((categoryId) => {
    setSelectedCategoryId(categoryId);
  }, []);

  const filterOptions = useMemo(() => [
    { value: 'hosts', label: t('reports.filters.hosts') },
    { value: 'metricCategories', label: t('reports.filters.metricCategories') },
  ], [t]);

  const labels = useMemo(() => ({
    leftPanelTitle: t('reports.filters.availableEntities'),
    middlePanelTitle: t('reports.filters.select'),
    rightPanelTitle: t('reports.filters.selectedEntities'),
    filterByLabel: t('reports.filters.metricsSelectionBy'),
    selectAll: t('common.selectAll'),
    clearAll: t('common.clearAll'),
    noItemsAvailable: selectionBy === 'hosts'
      ? t('reports.filters.noHostsAvailable')
      : t('reports.filters.noMetricCategoriesAvailable'),
    noItemsFound: t('reports.filters.noMetricsFound'),
    noItemsSelected: t('reports.filters.noEntitiesSelected'),
    noCategoriesAvailable: selectionBy === 'hosts'
      ? t('reports.filters.noHostsAvailable')
      : t('reports.filters.noMetricCategoriesAvailable'),
    selectCategoryFirst: selectionBy === 'hosts'
      ? t('reports.filters.selectHostFirst')
      : t('reports.filters.selectMetricCategoryFirst'),
  }), [t, selectionBy]);

  // Combine all metrics sources for EntityPanelSelector
  const combinedAvailableItems = useMemo(() => {
    const itemsMap = new Map();
    Object.values(allMetricsMap).forEach((data) => {
      itemsMap.set(data.item.id, data.item);
    });
    Object.values(metricsWithCategoryRef.current).forEach((data) => {
      if (!itemsMap.has(data.item.id)) {
        itemsMap.set(data.item.id, data.item);
      }
    });
    metricsForCategory.forEach((m) => {
      if (!itemsMap.has(m.id)) {
        itemsMap.set(m.id, m);
      }
    });
    return Array.from(itemsMap.values());
  }, [allMetricsMap, metricsForCategory]);

  // Build selected items map for grouped display in right panel
  const selectedItemsWithCategory = useMemo(() => {
    const result = {};
    selectedMetricIds.forEach((metricId) => {
      const metricData = allMetricsMap[metricId] || metricsWithCategoryRef.current[metricId];
      if (metricData) {
        result[metricId] = metricData;
      }
    });
    return result;
  }, [selectedMetricIds, allMetricsMap]);

  // Check both allMetricsMap and ref to ensure items are found regardless of source
  const handleSelectionChange = useCallback((newSelectedIds) => {
    const selectedItems = newSelectedIds
      .map((id) => {
        const data = allMetricsMap[id] || metricsWithCategoryRef.current[id];
        return data?.item;
      })
      .filter(Boolean);
    onSelectionChange(newSelectedIds, selectedItems);
  }, [onSelectionChange, allMetricsMap]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <EntityPanelSelector
      mode={LAYOUT_MODES.THREE_COLUMNS_WITH_FILTER}
      availableItems={combinedAvailableItems}
      selectedItemIds={selectedMetricIds}
      onSelectionChange={handleSelectionChange}
      // 3-column mode props
      categories={availableCategories}
      selectedCategoryId={selectedCategoryId}
      onCategorySelect={handleCategorySelect}
      middlePanelItems={metricsForCategory}
      middlePanelLoading={loadingMetrics}
      // Grouped display for selected items
      selectedItemsWithCategory={selectedItemsWithCategory}
      // Filter dropdown
      filterOptions={filterOptions}
      selectedFilterValue={selectionBy}
      onFilterChange={handleSelectionByChange}
      // Labels
      labels={labels}
      valueField="id"
    />
  );
}

MetricFilter.propTypes = {
  selectedMetricIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

export { MetricFilter };
export default MetricFilter;
